import { defineStore } from "pinia";
import type { TableColumnTypeCustom } from "@/types/claims";
import { computed, ref } from "vue";
import {
  initDefaultColumns,
  useTableColumns,
} from "@/composables/table/tableColumns";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import axios from "axios";
import { createNotification } from "@/tools/notification";

export interface MarkingCodesDocumentsTableItem {
  id: string;
  number: string;
  type: MarkingCodesDocumentType;
  status: MarkingCodesDocumentStatus;
  category: string;
  errorMessage: string;
  actions: null;
}

export enum MarkingCodesDocumentStatus {
  Created,
  Error,
  Accept,
  Success,
}

export enum MarkingCodesDocumentType {
  Into,
  From,
  Return,
}

export const MarkingCodesDocumentStatusTranslates: Record<
  MarkingCodesDocumentStatus,
  string
> = {
  [MarkingCodesDocumentStatus.Created]: "Создан",
  [MarkingCodesDocumentStatus.Error]: "Ошибка",
  [MarkingCodesDocumentStatus.Accept]: "Принят",
  [MarkingCodesDocumentStatus.Success]: "Успешно",
};

export const MarkingCodesDocumentTypeTranslates: Record<
  MarkingCodesDocumentType,
  string
> = {
  [MarkingCodesDocumentType.Into]: "Ввод в оборот",
  [MarkingCodesDocumentType.From]: "Вывод из оборота",
  [MarkingCodesDocumentType.Return]: "Возврат в оборот",
};

export const MarkingCodesDocumentStatusOptions = Object.entries(
  MarkingCodesDocumentStatusTranslates,
).map(([key, title]) => ({
  value:
    MarkingCodesDocumentStatus[key as keyof typeof MarkingCodesDocumentStatus],
  title,
}));

export const MarkingCodesDocumentTypeOptions = Object.entries(
  MarkingCodesDocumentTypeTranslates,
).map(([key, title]) => ({
  value: MarkingCodesDocumentType[key as keyof typeof MarkingCodesDocumentType],
  title,
}));
const INITIAL_FILTERS = {};

export const useMarkingCodesDocuments = defineStore(
  "markingCodesDocumentsStore",
  () => {
    const filters = ref({ ...INITIAL_FILTERS });
    const getDocumentsDataStatusLoad = ref<enumStatusLoad>(
      enumStatusLoad.NOT_STARTED,
    );

    const selectedRowKeys = ref<MarkingCodesDocumentsTableItem["id"][]>([]);

    const isDataLoading = computed(
      () => getDocumentsDataStatusLoad.value === enumStatusLoad.LOADING,
    );

    const pagination = ref({
      current: 1,
      total: 500,
      pageSize: 10,
      defaultPageSize: 10,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ["10", "20", "50", "100"],
      showTotal: (total: number, range: [number, number]) =>
        `${range[0]}-${range[1]} из ${total}`,
      onChangePagination: async () => {
        await loadData();
      },
    });
    const tableData = ref<MarkingCodesDocumentsTableItem[]>([]);
    const columnsConfigName = "baskitMarkingCodesDocumentsTable";

    const combineProductsBySizeColumns = ref<
      TableColumnTypeCustom<MarkingCodesDocumentsTableItem>[]
    >(
      initDefaultColumns<MarkingCodesDocumentsTableItem>([
        { dataIndex: "id", title: "ID" },
        { dataIndex: "number", title: "Номер документа" },
        { dataIndex: "type", title: "Тип документа" },
        { dataIndex: "status", title: "Статус" },
        { dataIndex: "category", title: "Категория" },
        { dataIndex: "errorMessage", title: "Сообщение об ошибке" },
        { dataIndex: "actions", title: "Действия" },
      ]),
    );

    const { columnsFiltered, dragList, initDragList } = useTableColumns(
      combineProductsBySizeColumns,
      columnsConfigName,
      tableData,
    );

    initDragList();

    const createTableData = (data: any) => {
      const result: MarkingCodesDocumentsTableItem[] = [];

      return result;
    };

    const loadData = async (tableFilters = filters.value) => {
      try {
        getDocumentsDataStatusLoad.value = enumStatusLoad.LOADING;

        const take = pagination.value.pageSize;
        const skip = (pagination.value.current - 1) * pagination.value.pageSize;

        const response = await new Promise((resolve) =>
          setTimeout(() => resolve({ data: { item1: [], item2: 1000 } }), 2000),
        );

        tableData.value = createTableData(response.data.item1);
        pagination.value.total = response.data.item2;
        getDocumentsDataStatusLoad.value = enumStatusLoad.LOADED;
        updateSelectedItems([]);
      } catch (error) {
        onError(error);
        getDocumentsDataStatusLoad.value = enumStatusLoad.ERROR;
      }
    };

    const onError = (err: unknown) => {
      if (axios.isAxiosError(err)) {
        createNotification(
          "error",
          err.response?.data?.detail,
          undefined,
          "topRight",
        );
      }
    };

    const updateSelectedItems = (
      selectedKeys: MarkingCodesDocumentsTableItem["id"][],
    ) => {
      selectedRowKeys.value = [...selectedKeys];
    };

    return {
      columnsFiltered,
      dragList,
      pagination,
      updateSelectedItems,
      getDocumentsDataStatusLoad,
      selectedRowKeys,
      isDataLoading,
      columnsConfigName,
      tableData,
      loadData,
    };
  },
);
